import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Download, MessageSquare, Trash2 } from "lucide-react";

interface Contact {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  status: "new" | "in_progress" | "resolved";
  adminNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ContactsResponse {
  contacts: Contact[];
  total: number;
  page: number;
  pageSize: number;
}

async function fetchContacts(
  statusFilter: string,
  page: number
): Promise<ContactsResponse> {
  const params = new URLSearchParams({ page: String(page), pageSize: "20" });
  if (statusFilter !== "all") params.set("status", statusFilter);
  const res = await fetch(`/api/admin/contacts?${params}`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to fetch contacts");
  return res.json();
}

async function deleteContact(id: string): Promise<void> {
  const res = await fetch(`/api/admin/contacts/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error.error || "Failed to delete contact");
  }
}

async function updateContact(
  id: string,
  data: { status?: Contact["status"]; adminNotes?: string }
): Promise<Contact> {
  const res = await fetch(`/api/admin/contacts/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || "Failed to update contact");
  }
  return res.json();
}

function StatusBadge({ status }: { status: Contact["status"] }) {
  const styles = {
    new: "bg-blue-50 text-blue-600",
    in_progress: "bg-amber-50 text-amber-600",
    resolved: "bg-emerald-50 text-emerald-600",
  };
  const dots = {
    new: "bg-blue-500",
    in_progress: "bg-amber-500",
    resolved: "bg-emerald-500",
  };
  const labels = {
    new: "New",
    in_progress: "In Progress",
    resolved: "Resolved",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${styles[status]}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dots[status]}`} />
      {labels[status]}
    </span>
  );
}

const formatDate = (s: string) =>
  new Date(s).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });

export default function AdminContacts() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [exporting, setExporting] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [dialogStatus, setDialogStatus] = useState<Contact["status"]>("new");
  const [dialogNotes, setDialogNotes] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["/api/admin/contacts", statusFilter, page],
    queryFn: () => fetchContacts(statusFilter, page),
    enabled: user?.role === "admin",
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteContact(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/contacts"] });
      toast({ title: "Contact Deleted", description: "The contact has been permanently deleted." });
      setConfirmDeleteId(null);
      setSelectedContact(null);
    },
    onError: (error: Error) => {
      toast({ title: "Delete Failed", description: error.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, status, adminNotes }: { id: string; status: Contact["status"]; adminNotes: string }) =>
      updateContact(id, { status, adminNotes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/contacts"] });
      toast({ title: "Contact updated", description: "The contact has been updated successfully." });
      setSelectedContact(null);
    },
    onError: (error: Error) => {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
    },
  });

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await fetch("/api/admin/contacts?pageSize=10000", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch contacts");
      const { contacts: all }: ContactsResponse = await res.json();

      const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
      const header = ["ID", "Name", "Email", "Subject", "Message", "Status", "Admin Notes", "Received"].join(";");
      const rows = all.map((c) =>
        [c.id, c.name, c.email, c.subject, c.message, c.status, c.adminNotes ?? "", formatDate(c.createdAt)]
          .map(String)
          .map(escape)
          .join(";")
      );
      const csv = [header, ...rows].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contacts-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: "Export failed", variant: "destructive" });
    } finally {
      setExporting(false);
    }
  };

  const openDetail = (contact: Contact) => {
    setSelectedContact(contact);
    setDialogStatus(contact.status);
    setDialogNotes(contact.adminNotes ?? "");
  };

  const handleFilterChange = (value: string) => {
    setStatusFilter(value);
    setPage(1);
    setSelectedContact(null);
  };

  if (user?.role !== "admin") {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="max-w-md bg-card rounded-3xl shadow-sm outline outline-1 outline-offset-[-1px] outline-border overflow-hidden">
          <div className="px-6 py-5">
            <h3 className="text-xl font-bold text-foreground">{"Access Denied"}</h3>
            <p className="text-sm text-slate-500 mt-1">{"You must be a platform administrator to view this page."}</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const contacts = data?.contacts ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 20));

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-extrabold tracking-tight text-slate-900">
          {"Contact Submissions"}
        </h1>
        <p className="text-slate-500 mt-2">{"Manage contact form submissions from users."}</p>
      </div>

      <div className="flex items-center justify-between gap-4">
        <Select value={statusFilter} onValueChange={handleFilterChange}>
          <SelectTrigger className="w-[200px] rounded-2xl bg-slate-50">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{"All"}</SelectItem>
            <SelectItem value="new">{"New"}</SelectItem>
            <SelectItem value="in_progress">{"In Progress"}</SelectItem>
            <SelectItem value="resolved">{"Resolved"}</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" className="rounded-2xl" onClick={exportCsv} disabled={exporting}>
          <Download className="h-4 w-4 mr-2" />
          {exporting ? "Exporting…" : "Export CSV"}
        </Button>
      </div>

      <div className="bg-card rounded-3xl shadow-[0px_4px_6px_-4px_rgba(226,232,240,0.50),0px_10px_15px_-3px_rgba(226,232,240,0.50)] outline outline-1 outline-offset-[-1px] outline-border overflow-hidden">
        <div className="px-6 py-5 border-b border-border/30">
          <div className="flex items-center gap-3">
            <div className="w-[43px] h-[43px] bg-primary/10 rounded-2xl flex items-center justify-center">
              <MessageSquare className="h-5 w-5 text-primary" />
            </div>
            <h3 className="text-xl font-bold text-foreground">
              {`${total} Contacts`}
            </h3>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {contacts.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{"No contacts found."}</p>
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide">{"Name"}</TableHead>
                    <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide">{"Email"}</TableHead>
                    <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide">{"Subject"}</TableHead>
                    <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide">{"Message"}</TableHead>
                    <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide">{"Status"}</TableHead>
                    <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide">{"Received"}</TableHead>
                    <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide text-right">{"Actions"}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contacts.map((c) => (
                    <TableRow key={c.id} className="border-t border-slate-100">
                      <TableCell className="font-semibold text-slate-900">{c.name}</TableCell>
                      <TableCell className="text-slate-700">{c.email}</TableCell>
                      <TableCell className="text-slate-700">{c.subject}</TableCell>
                      <TableCell className="text-slate-500 text-sm max-w-[200px] truncate">
                        {c.message.length > 80 ? `${c.message.slice(0, 80)}…` : c.message}
                      </TableCell>
                      <TableCell><StatusBadge status={c.status} /></TableCell>
                      <TableCell className="text-sm text-slate-500">{formatDate(c.createdAt)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="rounded-xl"
                            onClick={() => openDetail(c)}
                          >
                            <MessageSquare className="h-4 w-4 text-primary" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="rounded-xl text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => setConfirmDeleteId(c.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <Button
                variant="outline"
                size="sm"
                className="rounded-2xl"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                {"Previous"}
              </Button>
              <span className="text-sm text-slate-500">
                {`Page ${page} of ${totalPages}`}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="rounded-2xl"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                {"Next"}
              </Button>
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!selectedContact} onOpenChange={(open) => { if (!open) setSelectedContact(null); }}>
        <DialogContent className="sm:max-w-[540px] rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">
              {"Contact Details"}
            </DialogTitle>
          </DialogHeader>
          {selectedContact && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-2xl space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">{"Name"}</span>
                  <span className="font-semibold text-slate-900">{selectedContact.name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">{"Email"}</span>
                  <span className="font-semibold text-slate-900">{selectedContact.email}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">{"Subject"}</span>
                  <span className="font-semibold text-slate-900">{selectedContact.subject}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">{"Received"}</span>
                  <span className="font-semibold text-slate-900">{formatDate(selectedContact.createdAt)}</span>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-slate-700">{"Message"}</Label>
                <div className="p-3 bg-slate-50 rounded-2xl text-sm text-slate-700 whitespace-pre-wrap max-h-40 overflow-y-auto">
                  {selectedContact.message}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-slate-700">{"Status"}</Label>
                <Select value={dialogStatus} onValueChange={(v) => setDialogStatus(v as Contact["status"])}>
                  <SelectTrigger className="rounded-2xl bg-slate-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">{"New"}</SelectItem>
                    <SelectItem value="in_progress">{"In Progress"}</SelectItem>
                    <SelectItem value="resolved">{"Resolved"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-slate-700">{"Admin Notes"}</Label>
                <Textarea
                  placeholder={"Add internal notes..."}
                  value={dialogNotes}
                  onChange={(e) => setDialogNotes(e.target.value)}
                  className="bg-slate-50 rounded-2xl resize-none"
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              className="rounded-2xl text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive sm:mr-auto"
              onClick={() => selectedContact && setConfirmDeleteId(selectedContact.id)}
            >
              <Trash2 className="h-4 w-4 mr-1.5" />
              {"Delete"}
            </Button>
            <Button variant="outline" className="rounded-2xl" onClick={() => setSelectedContact(null)}>
              {"Cancel"}
            </Button>
            <Button
              className="rounded-2xl"
              disabled={updateMutation.isPending}
              onClick={() =>
                selectedContact &&
                updateMutation.mutate({ id: selectedContact.id, status: dialogStatus, adminNotes: dialogNotes })
              }
            >
               {updateMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!confirmDeleteId} onOpenChange={(open) => { if (!open) setConfirmDeleteId(null); }}>
        <DialogContent className="sm:max-w-[400px] rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">
              {"Delete Contact?"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">{"This will permanently delete the contact message. This action cannot be undone."}</p>
          <DialogFooter>
            <Button variant="outline" className="rounded-2xl" onClick={() => setConfirmDeleteId(null)}>
              {"Cancel"}
            </Button>
            <Button
              variant="destructive"
              className="rounded-2xl"
              disabled={deleteMutation.isPending}
              onClick={() => confirmDeleteId && deleteMutation.mutate(confirmDeleteId)}
            >
              <Trash2 className="h-4 w-4 mr-1.5" />
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
